/**
 * A cell of the grid. `bg-surface`, no shadow, no margin, no rounded corner —
 * the canvas showing through the 1px gutters *is* the divider (components.md
 * §1), so a panel that adds any of the three breaks the one thing the layout
 * does.
 *
 * `header` is the optional `caption` strip a panel wears when it needs to name
 * itself — `settled · btc-updown-1m-1786637400` on a deep link into a resolved
 * market, for
 * instance. It is part of the panel rather than a separate component because it
 * must not scroll with the panel's body.
 */

import type { ReactNode } from 'react'
import { cx } from '@/lib/cx'

export interface PanelProps {
  header?: ReactNode
  /** Right-hand side of the header strip — a tab set, a mode toggle. */
  headerRight?: ReactNode
  children: ReactNode
  /** The panel's body scrolls; the page never does. */
  scroll?: boolean
  className?: string
  bodyClassName?: string
}

export function Panel({
  header,
  headerRight,
  children,
  scroll = false,
  className,
  bodyClassName,
}: PanelProps) {
  return (
    <section className={cx('flex min-h-0 min-w-0 flex-col bg-bg-surface', className)}>
      {header || headerRight ? (
        <header className="flex h-[var(--ud-row-height)] shrink-0 items-center justify-between gap-2 border-b border-border-subtle px-2 text-caption text-text-secondary">
          <span className="truncate">{header}</span>
          {headerRight ? <span className="shrink-0">{headerRight}</span> : null}
        </header>
      ) : null}
      <div className={cx('min-h-0 flex-1', scroll && 'ud-scroll', bodyClassName)}>{children}</div>
    </section>
  )
}
